// AI判定リザルト演出の効果音と振動。
//
// 効果音は public/sfx/ の素材ファイル（効果音ラボ）を優先して鳴らし、
// 読み込めないものは Web Audio の合成音で代用する（ドラムロールは素材のみ）。
//
// ブラウザの自動再生制限のため、ユーザー操作（AI判定ボタンのクリック）の中で
// unlockAudio() を呼んで AudioContext を起こし、素材の読み込みも始めておく。
import type { CutinTier } from './ai-result-tier'
import { isEffectSoundEnabled } from './effectSettings'

type AudioContextCtor = typeof AudioContext

let ctx: AudioContext | null = null
const active = new Set<AudioScheduledSourceNode>()
/** 鳴っているゴゴゴ（昇格時に強めるため保持） */
let rumble: { oscs: { osc: OscillatorNode; mult: number }[]; master: GainNode; endTime: number } | null = null

function audioContextCtor(): AudioContextCtor | undefined {
  // 古い iOS Safari は接頭辞付き
  return window.AudioContext ?? (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext
}

/** ユーザー操作の中で呼ぶ。以降の効果音再生を許可させる。 */
export function unlockAudio(): void {
  if (!isEffectSoundEnabled()) return
  const Ctor = audioContextCtor()
  if (!Ctor) return
  ctx ??= new Ctor()
  if (ctx.state === 'suspended') void ctx.resume()
  loadSfx(ctx)
  // iOS は無音でも操作中に1度鳴らしておくと確実に解放される
  const src = ctx.createBufferSource()
  src.buffer = ctx.createBuffer(1, 1, 22050)
  src.connect(ctx.destination)
  src.start(0)
}

function readyContext(): AudioContext | null {
  if (!isEffectSoundEnabled() || !ctx || ctx.state !== 'running') return null
  return ctx
}

function track(node: AudioScheduledSourceNode): void {
  active.add(node)
  node.addEventListener('ended', () => active.delete(node))
}

/** 波形を歪ませる曲線（tanh）。倍音を足して小さなスピーカーでも聞こえるようにする。 */
function driveCurve(amount: number): Float32Array<ArrayBuffer> {
  const n = 1024
  const curve = new Float32Array(new ArrayBuffer(n * 4))
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1
    curve[i] = Math.tanh(amount * x) / Math.tanh(amount)
  }
  return curve
}

/** 低いほど重く、音量も大きい（freq は基音。倍音側は freq×2 で鳴らす） */
const RUMBLE_PARAMS: Record<CutinTier, { freq: number; gain: number }> = {
  red: { freq: 55, gain: 0.35 },
  gold: { freq: 46, gain: 0.45 },
  rainbow: { freq: 39, gain: 0.55 },
}

/** 「ゴッ」1粒の間隔。最初はゆっくり、終わりに向けて速くなる */
const GRAIN_START_SEC = 1 / 6
const GRAIN_END_SEC = 1 / 10

/**
 * ゴゴゴ…
 * - 深い低音（サイン波・基音）：イヤホン・スピーカー向け
 * - 歪ませた鋸波（基音×2）＋ブラウンノイズを 100〜400Hz 付近に寄せる：スマホのスピーカーでも聞こえる帯域
 * - 「ゴッ、ゴッ」と1粒ずつ立ち上がって減衰する音量の形。粒はだんだん速く、全体もだんだん大きく
 */
function synthRumble(tier: CutinTier, durationMs: number): void {
  const c = readyContext()
  if (!c) return
  const { freq, gain } = RUMBLE_PARAMS[tier]
  const now = c.currentTime
  const dur = durationMs / 1000
  const end = now + dur

  // 粒の音量の形
  const grain = c.createGain()
  grain.gain.setValueAtTime(0.15, now)
  for (let t = now; t < end - 0.05; ) {
    const progress = (t - now) / dur
    const interval = GRAIN_START_SEC + (GRAIN_END_SEC - GRAIN_START_SEC) * progress
    grain.gain.setValueAtTime(0.15, t)
    grain.gain.linearRampToValueAtTime(1, t + 0.012)
    grain.gain.exponentialRampToValueAtTime(0.15, t + interval * 0.85)
    t += interval
  }

  const master = c.createGain()
  master.gain.setValueAtTime(gain * 0.3, now)
  master.gain.linearRampToValueAtTime(gain, now + dur * 0.9)
  master.gain.linearRampToValueAtTime(0.0001, end)
  grain.connect(master).connect(c.destination)

  // 深い低音
  const sub = c.createOscillator()
  sub.type = 'sine'
  sub.frequency.value = freq
  const subGain = c.createGain()
  subGain.gain.value = 0.6
  sub.connect(subGain).connect(grain)

  // 倍音（歪み → 100〜400Hz 付近）
  const body = c.createOscillator()
  body.type = 'sawtooth'
  body.frequency.value = freq * 2
  const drive = c.createWaveShaper()
  drive.curve = driveCurve(4)
  const bodyBand = c.createBiquadFilter()
  bodyBand.type = 'bandpass'
  bodyBand.frequency.value = 220
  bodyBand.Q.value = 0.8
  const bodyGain = c.createGain()
  bodyGain.gain.value = 0.5
  body.connect(drive).connect(bodyBand).connect(bodyGain).connect(grain)

  // ざらつき（ブラウンノイズ）
  const length = Math.max(1, Math.floor(c.sampleRate * dur))
  const buffer = c.createBuffer(1, length, c.sampleRate)
  const data = buffer.getChannelData(0)
  let last = 0
  for (let i = 0; i < length; i++) {
    last = (last + 0.02 * (Math.random() * 2 - 1)) / 1.02
    data[i] = last * 3.5
  }
  const noise = c.createBufferSource()
  noise.buffer = buffer
  const noiseBand = c.createBiquadFilter()
  noiseBand.type = 'bandpass'
  noiseBand.frequency.value = 250
  noiseBand.Q.value = 0.7
  const noiseGain = c.createGain()
  noiseGain.gain.value = 0.6
  noise.connect(noiseBand).connect(noiseGain).connect(grain)

  for (const node of [sub, body, noise]) {
    node.start(now)
    node.stop(end)
    track(node)
  }
  rumble = { oscs: [{ osc: sub, mult: 1 }, { osc: body, mult: 2 }], master, endTime: end }
  sub.addEventListener('ended', () => {
    if (rumble?.oscs[0]?.osc === sub) rumble = null
  })
}

/** 鳴っているゴゴゴを rampSec 秒かけて tier の重さ（低さ・音量）まで引き上げる。昇格の溜めで呼ぶ。 */
function synthEscalateRumble(tier: CutinTier, rampSec = 0.15): void {
  const c = readyContext()
  if (!c || !rumble) return
  const { freq, gain } = RUMBLE_PARAMS[tier]
  const now = c.currentTime
  const { oscs, master, endTime } = rumble
  if (now >= endTime) return
  const peakAt = Math.min(now + rampSec, endTime)

  for (const { osc, mult } of oscs) {
    osc.frequency.cancelScheduledValues(now)
    osc.frequency.setValueAtTime(osc.frequency.value, now)
    osc.frequency.exponentialRampToValueAtTime(freq * mult, peakAt)
  }

  master.gain.cancelScheduledValues(now)
  master.gain.setValueAtTime(master.gain.value, now)
  master.gain.linearRampToValueAtTime(gain, peakAt)
  master.gain.setValueAtTime(gain, Math.max(peakAt, endTime - 0.1))
  master.gain.linearRampToValueAtTime(0.0001, endTime)
}

/** 閃光の「ドン」（急降下するサイン波）。volume は 0〜1。 */
function synthImpact(volume = 1): void {
  const c = readyContext()
  if (!c) return
  const now = c.currentTime
  const osc = c.createOscillator()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(140, now)
  osc.frequency.exponentialRampToValueAtTime(35, now + 0.35)
  const amp = c.createGain()
  amp.gain.setValueAtTime(Math.max(0.0001, volume), now)
  amp.gain.exponentialRampToValueAtTime(0.0001, now + 0.4)
  osc.connect(amp).connect(c.destination)
  osc.start(now)
  osc.stop(now + 0.4)
  track(osc)
}

/** 昇格の「ダンッ」の重さ（虹は低く長く） */
const UPGRADE_HIT_PARAMS: Record<CutinTier, { from: number; to: number; decaySec: number }> = {
  red: { from: 180, to: 55, decaySec: 0.35 },
  gold: { from: 180, to: 55, decaySec: 0.35 },
  rainbow: { from: 160, to: 45, decaySec: 0.5 },
}

/**
 * 昇格の「ダンッ」。
 * - アタック：短いノイズ（1〜3kHz）で叩いた瞬間の硬さ
 * - 胴鳴り：急降下するサイン波で「ドン」の芯
 * - 歪み：胴鳴りを歪ませた倍音を足し、スマホのスピーカーでも存在感を出す
 */
function synthUpgradeHit(tier: CutinTier): void {
  const c = readyContext()
  if (!c) return
  const { from, to, decaySec } = UPGRADE_HIT_PARAMS[tier]
  const now = c.currentTime
  const out = c.createGain()
  out.gain.value = 0.8
  out.connect(c.destination)

  // アタック
  const clickLen = Math.floor(c.sampleRate * 0.025)
  const clickBuf = c.createBuffer(1, clickLen, c.sampleRate)
  const clickData = clickBuf.getChannelData(0)
  for (let i = 0; i < clickLen; i++) clickData[i] = (Math.random() * 2 - 1) * (1 - i / clickLen)
  const click = c.createBufferSource()
  click.buffer = clickBuf
  const clickBand = c.createBiquadFilter()
  clickBand.type = 'bandpass'
  clickBand.frequency.value = 2000
  clickBand.Q.value = 1
  const clickGain = c.createGain()
  clickGain.gain.value = 0.6
  click.connect(clickBand).connect(clickGain).connect(out)
  click.start(now)
  track(click)

  // 胴鳴り
  const body = c.createOscillator()
  body.type = 'sine'
  body.frequency.setValueAtTime(from, now)
  body.frequency.exponentialRampToValueAtTime(to, now + decaySec * 0.6)
  const bodyEnv = c.createGain()
  bodyEnv.gain.setValueAtTime(1, now)
  bodyEnv.gain.exponentialRampToValueAtTime(0.0001, now + decaySec)
  body.connect(bodyEnv)
  bodyEnv.connect(out)

  // 歪み（胴鳴りと並列に足す）
  const drive = c.createWaveShaper()
  drive.curve = driveCurve(3)
  const driveGain = c.createGain()
  driveGain.gain.value = 0.5
  bodyEnv.connect(drive).connect(driveGain).connect(out)

  body.start(now)
  body.stop(now + decaySec)
  track(body)
}

/** 段階昇格の「キュイン」（上昇するサイン波）。level が高いほど音程が上がる。 */
function synthKyuin(level: number): void {
  const c = readyContext()
  if (!c) return
  const now = c.currentTime
  const base = 700 * Math.pow(1.25, Math.max(0, level - 1))
  const osc = c.createOscillator()
  osc.type = 'sine'
  osc.frequency.setValueAtTime(base, now)
  osc.frequency.exponentialRampToValueAtTime(base * 2.5, now + 0.18)
  const amp = c.createGain()
  amp.gain.setValueAtTime(0.0001, now)
  amp.gain.exponentialRampToValueAtTime(0.35, now + 0.02)
  amp.gain.exponentialRampToValueAtTime(0.0001, now + 0.3)
  osc.connect(amp).connect(c.destination)
  osc.start(now)
  osc.stop(now + 0.3)
  track(osc)
}

/** 花吹雪の粒の数・時間 */
const CONFETTI_SPARKLES = 18
const CONFETTI_SPREAD_SEC = 1.5

/**
 * レベルアップの花吹雪。
 * - パンッ：ハイパスを通したノイズの破裂音
 * - シャラララ：2〜6kHz の短いサイン波の粒をばらけて鳴らし、後半ほど小さくする
 */
function synthConfetti(): void {
  const c = readyContext()
  if (!c) return
  const now = c.currentTime
  const out = c.createGain()
  out.gain.value = 0.3
  out.connect(c.destination)

  // パンッ
  const popLen = Math.floor(c.sampleRate * 0.12)
  const popBuf = c.createBuffer(1, popLen, c.sampleRate)
  const popData = popBuf.getChannelData(0)
  for (let i = 0; i < popLen; i++) popData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / popLen, 3)
  const pop = c.createBufferSource()
  pop.buffer = popBuf
  const popFilter = c.createBiquadFilter()
  popFilter.type = 'highpass'
  popFilter.frequency.value = 800
  const popGain = c.createGain()
  popGain.gain.value = 1.5
  pop.connect(popFilter).connect(popGain).connect(out)
  pop.start(now)
  track(pop)

  // シャラララ
  for (let i = 0; i < CONFETTI_SPARKLES; i++) {
    const at = now + 0.05 + Math.random() * CONFETTI_SPREAD_SEC
    const fade = 1 - (at - now) / (CONFETTI_SPREAD_SEC + 0.1) // 後半ほど小さく
    const osc = c.createOscillator()
    osc.type = 'sine'
    osc.frequency.value = 2000 + Math.random() * 4000
    const amp = c.createGain()
    amp.gain.setValueAtTime(0.0001, at)
    amp.gain.exponentialRampToValueAtTime(Math.max(0.0002, 0.5 * fade), at + 0.005)
    amp.gain.exponentialRampToValueAtTime(0.0001, at + 0.12)
    osc.connect(amp).connect(out)
    osc.start(at)
    osc.stop(at + 0.12)
    track(osc)
  }
}

// ───────────── 素材ファイル ─────────────

type SfxName = 'rumble' | 'upgrade' | 'flash' | 'tierUp' | 'hit' | 'confetti' | 'roll' | 'rollEnd'

/** public/sfx/ の素材（出典：効果音ラボ。割り当ては決定仕様記録を参照） */
const SFX_FILES: Record<SfxName, string> = {
  rumble: 'rumble.mp3', // ゴゴゴゴ
  upgrade: 'upgrade.mp3', // ショット命中
  flash: 'flash.mp3', // 決定ボタンを押す20
  tierUp: 'tier-up.mp3', // 和太鼓でドン
  hit: 'hit.mp3', // ジャジャーン
  confetti: 'confetti.mp3', // 歓声と拍手1
  roll: 'roll.mp3', // ティンパニロール
  rollEnd: 'roll-end.mp3', // ロールの閉め
}

/** 素材ごとの音量（0〜1）。聞きながらここで調整する */
export const SFX_VOLUME: Record<SfxName, number> = {
  rumble: 0.8,
  upgrade: 1,
  flash: 0.9,
  tierUp: 0.6,
  hit: 0.8,
  confetti: 0.7,
  roll: 0.6,
  rollEnd: 0.9,
}

/** 読み込み結果。null は「無い・読めない」（合成音で代用する） */
const buffers = new Map<SfxName, AudioBuffer | null>()
let loading: Promise<void> | null = null

/** 素材を並行して1回だけ読み込む（失敗も含めてキャッシュする） */
function loadSfx(c: AudioContext): void {
  loading ??= Promise.all(
    (Object.keys(SFX_FILES) as SfxName[]).map(async (name) => {
      try {
        const res = await fetch(`${import.meta.env.BASE_URL}sfx/${SFX_FILES[name]}`)
        if (!res.ok) throw new Error(res.statusText)
        // 開発サーバーは無いファイルにも index.html を返すが、decode に失敗して null になる
        buffers.set(name, await c.decodeAudioData(await res.arrayBuffer()))
      } catch {
        buffers.set(name, null)
      }
    }),
  ).then(() => undefined)
}

type Voice = { src: AudioBufferSourceNode; gain: GainNode }

/** 素材を鳴らす。無ければ null（呼び出し側で合成音に切り替える） */
function playFile(c: AudioContext, name: SfxName, { rate = 1, loop = false } = {}): Voice | null {
  const buffer = buffers.get(name)
  if (!buffer) return null
  const src = c.createBufferSource()
  src.buffer = buffer
  src.loop = loop
  src.playbackRate.value = rate
  const gain = c.createGain()
  gain.gain.value = SFX_VOLUME[name]
  src.connect(gain).connect(c.destination)
  src.start()
  track(src)
  return { src, gain }
}

function fadeOut(c: AudioContext, voice: Voice, sec = 0.15): void {
  const now = c.currentTime
  voice.gain.gain.cancelScheduledValues(now)
  voice.gain.gain.setValueAtTime(voice.gain.gain.value, now)
  voice.gain.gain.linearRampToValueAtTime(0.0001, now + sec)
  try {
    voice.src.stop(now + sec + 0.02)
  } catch {
    // 既に停止済み
  }
}

// ───────────── 演出から呼ぶ効果音（素材 → 無ければ合成音） ─────────────

/** ゴゴゴの重さ（素材）：再生速度を下げるほど低く重い */
const RUMBLE_FILE_PARAMS: Record<CutinTier, { rate: number; gain: number }> = {
  red: { rate: 1, gain: 0.7 },
  gold: { rate: 0.92, gain: 0.85 },
  rainbow: { rate: 0.85, gain: 1 },
}

/** 鳴っているゴゴゴ（素材版）。昇格の溜めで重くするため保持 */
let fileRumble: { voice: Voice; endTime: number } | null = null

/** ゴゴゴ… を durationMs だけ鳴らす（フェードイン → だんだん大きく → フェードアウト） */
export function playRumble(tier: CutinTier, durationMs: number): void {
  const c = readyContext()
  if (!c) return
  const { rate, gain } = RUMBLE_FILE_PARAMS[tier]
  const voice = playFile(c, 'rumble', { rate, loop: true })
  if (!voice) {
    synthRumble(tier, durationMs)
    return
  }
  const now = c.currentTime
  const end = now + durationMs / 1000
  const peak = SFX_VOLUME.rumble * gain
  voice.gain.gain.setValueAtTime(0.0001, now)
  voice.gain.gain.linearRampToValueAtTime(peak * 0.6, now + 0.3)
  voice.gain.gain.linearRampToValueAtTime(peak, Math.max(now + 0.3, end - 0.2))
  voice.gain.gain.linearRampToValueAtTime(0.0001, end)
  voice.src.stop(end)
  fileRumble = { voice, endTime: end }
  voice.src.addEventListener('ended', () => {
    if (fileRumble?.voice === voice) fileRumble = null
  })
}

/** 鳴っているゴゴゴを rampSec 秒かけて tier の重さまで引き上げる。昇格の溜めで呼ぶ。 */
export function escalateRumble(tier: CutinTier, rampSec = 0.15): void {
  const c = readyContext()
  if (!c) return
  if (!fileRumble) {
    synthEscalateRumble(tier, rampSec)
    return
  }
  const { voice, endTime } = fileRumble
  const now = c.currentTime
  if (now >= endTime) return
  const { rate, gain } = RUMBLE_FILE_PARAMS[tier]
  const peakAt = Math.min(now + rampSec, endTime)
  const peak = SFX_VOLUME.rumble * gain

  voice.src.playbackRate.cancelScheduledValues(now)
  voice.src.playbackRate.setValueAtTime(voice.src.playbackRate.value, now)
  voice.src.playbackRate.linearRampToValueAtTime(rate, peakAt)

  voice.gain.gain.cancelScheduledValues(now)
  voice.gain.gain.setValueAtTime(voice.gain.gain.value, now)
  voice.gain.gain.linearRampToValueAtTime(peak, peakAt)
  voice.gain.gain.setValueAtTime(peak, Math.max(peakAt, endTime - 0.2))
  voice.gain.gain.linearRampToValueAtTime(0.0001, endTime)
}

/** gain を rampSec 秒で現在値の factor 倍にし、endTime のフェードアウトを予約し直す */
function rampGainTo(c: AudioContext, gain: GainNode, factor: number, rampSec: number, endTime: number): void {
  const now = c.currentTime
  if (now >= endTime) return
  const target = Math.max(0.0001, gain.gain.value * factor)
  const at = Math.min(now + rampSec, endTime)
  gain.gain.cancelScheduledValues(now)
  gain.gain.setValueAtTime(gain.gain.value, now)
  gain.gain.linearRampToValueAtTime(target, at)
  gain.gain.setValueAtTime(target, Math.max(at, endTime - 0.2))
  gain.gain.linearRampToValueAtTime(0.0001, endTime)
}

/** 昇格の発展で、ゴゴゴを一瞬静める（嵐の前の静けさ）。続く escalateRumble で膨らませる */
export function duckRumble(factor = 0.5, rampSec = 0.2): void {
  const c = readyContext()
  if (!c) return
  if (fileRumble) rampGainTo(c, fileRumble.voice.gain, factor, rampSec, fileRumble.endTime)
  else if (rumble) rampGainTo(c, rumble.master, factor, rampSec, rumble.endTime)
}

/** ゴゴゴだけを止める（カットインが消えるとき。閃光など他の音は鳴らし切る） */
export function stopRumble(): void {
  const c = readyContext()
  if (c && fileRumble) fadeOut(c, fileRumble.voice, 0.05)
  if (c && rumble) {
    const now = c.currentTime
    rumble.master.gain.cancelScheduledValues(now)
    rumble.master.gain.setValueAtTime(rumble.master.gain.value, now)
    rumble.master.gain.linearRampToValueAtTime(0.0001, now + 0.05)
  }
  fileRumble = null
  rumble = null
}

/** 昇格の「ダンッ」。虹への昇格は低く重くする */
export function playUpgradeHit(tier: CutinTier): void {
  const c = readyContext()
  if (!c) return
  if (!playFile(c, 'upgrade', { rate: tier === 'rainbow' ? 0.85 : 1 })) synthUpgradeHit(tier)
}

/** カットイン最後の閃光 */
export function playFlash(): void {
  const c = readyContext()
  if (!c) return
  if (!playFile(c, 'flash')) synthImpact()
}

/**
 * ハイライトのカードが出たとき。素材（ジャジャーン）は長いので1枚目だけ鳴らす。
 * 素材が無いときは従来どおり毎回、軽い合成音の「ドン」。
 */
export function playHighlightHit(index: number): void {
  const c = readyContext()
  if (!c) return
  if (buffers.get('hit')) {
    if (index === 0) playFile(c, 'hit')
    return
  }
  synthImpact(0.5)
}

/** 段階アップの再生速度（level 1〜4。和太鼓は上げすぎると軽くなるので控えめ） */
const TIER_UP_RATES = [1, 1, 1.05, 1.1, 1.15]
let tierUpVoice: Voice | null = null

/** ポイントの段階アップ（和太鼓。素材が無ければ合成のキュイン）。level が高いほど少し高く。余韻が長いので前の音はフェードアウトさせる */
export function playTierUp(level: number): void {
  const c = readyContext()
  if (!c) return
  if (tierUpVoice) fadeOut(c, tierUpVoice)
  tierUpVoice = playFile(c, 'tierUp', { rate: TIER_UP_RATES[Math.min(level, TIER_UP_RATES.length - 1)] })
  if (!tierUpVoice) synthKyuin(level)
}

/** レベルアップの花吹雪 */
export function playConfetti(): void {
  const c = readyContext()
  if (!c) return
  if (!playFile(c, 'confetti')) synthConfetti()
}

let rollVoice: Voice | null = null

/** カウントアップ中のドラムロール（素材が無ければ鳴らさない） */
export function startRoll(): void {
  const c = readyContext()
  if (!c) return
  if (rollVoice) fadeOut(c, rollVoice, 0.05)
  rollVoice = playFile(c, 'roll', { loop: true })
}

export function stopRoll(): void {
  const c = readyContext()
  if (c && rollVoice) fadeOut(c, rollVoice, 0.08)
  rollVoice = null
}

/** 数え終わりの「ロールの閉め」（素材が無ければ鳴らさない） */
export function playRollEnd(): void {
  const c = readyContext()
  if (!c) return
  playFile(c, 'rollEnd')
}

/** 対応端末のみ振動（iOS Safari は非対応で何もしない） */
export function vibrate(pattern: number | number[]): void {
  if (!isEffectSoundEnabled() || !('vibrate' in navigator)) return
  navigator.vibrate(pattern)
}

/** 再生中の効果音と振動を止める（スキップ時など） */
export function stopAll(): void {
  for (const node of active) {
    try {
      node.stop()
    } catch {
      // 既に停止済み
    }
  }
  active.clear()
  rumble = null
  fileRumble = null
  tierUpVoice = null
  rollVoice = null
  if ('vibrate' in navigator) navigator.vibrate(0)
}
